/**
 * The `?type=` values the Dennis queue accepts, and how each maps to a GraphQL filter.
 *
 * Every question type contributes its own value, except multiple choice, which splits into three:
 * the plain `multiple-choice` value that existing bookmarks carry, plus two that narrow it by the
 * question's "Multiple correct answers" setting.
 */
import {QUESTION_TYPES} from 'client/constants';

import type {QuestionFilterInput} from 'gql/types';

/** `client/constants` is untyped JavaScript, so narrow what this module reads out of it. */
const questionTypes = Object.values(QUESTION_TYPES) as {name: string; title: string}[];

export const MULTIPLE_CHOICE_TYPE: string = QUESTION_TYPES.MULTIPLE_CHOICE.name;
export const MULTIPLE_CHOICE_SINGLE_SELECT_TYPE = 'multiple-choice-single-select';
export const MULTIPLE_CHOICE_MULTI_SELECT_TYPE = 'multiple-choice-multi-select';

interface QuestionTypeOption {
  id: string | null;
  name: string;
}

const MULTIPLE_CHOICE_OPTIONS: QuestionTypeOption[] = [
  {id: MULTIPLE_CHOICE_TYPE, name: 'Multiple Choice - All'},
  {id: MULTIPLE_CHOICE_SINGLE_SELECT_TYPE, name: 'Multiple Choice - Single Select'},
  {id: MULTIPLE_CHOICE_MULTI_SELECT_TYPE, name: 'Multiple Choice - Multi Select'}
];

/**
 * Question Type dropdown options, alphabetical by label with the three multiple-choice options
 * expanded in place so they stay together and in All / Single Select / Multi Select order.
 */
export const QUESTION_TYPE_OPTIONS: QuestionTypeOption[] = [
  {id: null, name: 'All'},
  ...questionTypes
    .map((type) => ({id: type.name, name: type.title}))
    .sort((a, b) => a.name.localeCompare(b.name))
    .flatMap((option) => (option.id === MULTIPLE_CHOICE_TYPE ? MULTIPLE_CHOICE_OPTIONS : [option]))
];

export function isValidQuestionTypeParameter(value: string): boolean {
  return QUESTION_TYPE_OPTIONS.some((option) => option.id === value);
}

/**
 * Builds the GraphQL filters for a `?type=` value. The two select-mode values are queue-only, so
 * they resolve to the `multiple-choice` question type plus the question's `isMultiSelect` flag.
 */
export function buildQuestionTypeFilters(value: string): QuestionFilterInput[] {
  switch (value) {
    case MULTIPLE_CHOICE_SINGLE_SELECT_TYPE:
      return [{questionType: {eq: MULTIPLE_CHOICE_TYPE}}, {isMultiSelect: {eq: false}}];
    case MULTIPLE_CHOICE_MULTI_SELECT_TYPE:
      return [{questionType: {eq: MULTIPLE_CHOICE_TYPE}}, {isMultiSelect: {eq: true}}];
    default:
      return [{questionType: {eq: value}}];
  }
}
